import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { TIDS } from "@/constants/testIds";
import { useAuth } from "@/lib/auth";
import { useSocial } from "@/lib/social";
import { api } from "@/lib/api";
import { UserPlus, Ban, VolumeX, Volume2, MessageSquare, X, LogOut, ShieldOff } from "lucide-react";
import EmotePicker, { EmoteButton } from "@/components/EmotePicker";
import ChatMessageText from "@/components/ChatMessageText";
import BlingUsername from "@/components/BlingUsername";
// Founder chat badge temporarily disabled. See Profile.jsx for details.

export default function Chat({ ws, peerId, color, channel, isStreamer }) {
  const { user } = useAuth();
  const social = useSocial();
  const [messages, setMessages] = useState([]);
  const [presence, setPresence] = useState({ users: [], count: 0 });
  const [text, setText] = useState("");
  const [menu, setMenu] = useState(null);
  const [notice, setNotice] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!ws) return;
    const handler = (e) => {
      const msg = JSON.parse(e.data);
      if (msg.type === "chat") setMessages((m) => [...m.slice(-199), msg.message]);
      else if (msg.type === "sub_event") {
        // Welcome-new-sub system event → renders as a highlighted chat row
        setMessages((m) => [...m.slice(-199), { ...msg.message, _kind: "sub_event" }]);
      }
      else if (msg.type === "presence") setPresence({ users: msg.users || [], count: msg.count || 0 });
      else if (msg.type === "kicked") setNotice("You've been kicked by the streamer.");
      else if (msg.type === "banned") setNotice("You've been banned from this channel.");
      else if (msg.type === "chat_rejected") {
        setNotice(msg.reason || "Message blocked by auto-mod.");
        toast.error(msg.reason || "Message blocked by auto-mod.");
      }
    };
    ws.addEventListener("message", handler);
    return () => ws.removeEventListener("message", handler);
  }, [ws]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menu]);

  const insertAtCaret = (fragment) => {
    const el = inputRef.current;
    if (!el) { setText(t => t + fragment); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + fragment + text.slice(end);
    setText(next);
    // Restore caret position after the inserted fragment
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + fragment.length;
        el.setSelectionRange(pos, pos);
      } catch { /* noop */ }
    });
  };

  const send = () => {
    const t = text.trim();
    if (!t) return;
    if (!ws) {
      toast.error("Chat isn't connected yet — hold on a second.");
      return;
    }
    if (ws.readyState !== 1) {
      toast.error("Reconnecting to chat…");
      return;
    }
    if (!user) {
      toast.error("Log in to chat.");
      return;
    }
    try {
      ws.send(JSON.stringify({ type: "chat", text: t }));
      setText("");
      setPickerOpen(false);
    } catch (e) {
      toast.error("Couldn't send message — try again.");
    }
  };

  const openMenu = (username, event) => {
    if (!user || username === user.username) return;
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    // Clamp so the menu (~200px wide, ~240px tall) never clips the viewport.
    const MENU_W = 200;
    const MENU_H = 240;
    const x = Math.min(rect.left, window.innerWidth - MENU_W - 8);
    const y = rect.bottom + 4 + MENU_H > window.innerHeight
      ? Math.max(8, rect.top - MENU_H - 4)  // flip above the target
      : rect.bottom + 4;
    setMenu({ username, x: Math.max(8, x), y });
  };

  const visibleMsgs = messages.filter(m =>
    !social.isBlocked(m.username) && !social.isMuted(m.username)
  );

  return (
    <div className="flex flex-col h-full bg-[#18181C] border border-white/10 rounded-md overflow-hidden relative">
      {notice && (
        <div className="px-4 py-2 border-b border-white/10 bg-[#FF1053]/10 text-[color:var(--sc-pink)] text-xs font-bold flex items-center justify-between gap-2">
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="text-[color:var(--sc-pink)]/70 hover:text-white">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="sc-label">Stream Chat</div>
        <div className="text-xs text-zinc-400 sc-mono">{presence.count} watching</div>
      </div>
      <div data-testid={TIDS.chatList} ref={listRef} className="flex-1 overflow-y-auto py-2">
        {visibleMsgs.length === 0 && (
          <div className="text-xs text-zinc-500 px-4 py-3">Welcome to chat. Say hi!</div>
        )}
        {visibleMsgs.map((m) => (
          m._kind === "sub_event" ? (
            <div
              key={m.id}
              className="chat-msg my-1 px-2 py-1.5 rounded-sm border"
              style={{ borderColor: "rgba(255,16,83,0.35)", background: "linear-gradient(90deg, rgba(255,16,83,0.15), rgba(255,89,0,0.10) 60%, transparent)" }}
              data-testid={`chat-sub-event-${m.subscriber_username}`}
            >
              <span className="text-[9px] sc-mono font-black tracking-widest uppercase mr-2 px-1 py-0.5 rounded-sm"
                    style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)", color: "#fff" }}>NEW SUB</span>
              <span className="text-sm">{m.text || `🎉 @${m.subscriber_username} just subscribed to the channel!`}</span>
            </div>
          ) : (
          <div key={m.id} className="chat-msg">
            <BlingUsername
              username={m.username}
              defaultColor={m.color || "#FF1053"}
              onClick={(e) => openMenu(m.username, e)}
              testid={`chat-user-${m.username}`}
            />
            {m.is_channel_sub && (
              <span
                className="text-[8px] sc-mono font-bold px-1 py-0.5 rounded-sm mr-1 align-middle"
                style={{ background: "linear-gradient(135deg, #FF1053, #FF5900)", color: "#fff", letterSpacing: "0.06em" }}
                title="Channel subscriber"
                data-testid={`chat-sub-badge-${m.username}`}
              >SUB</span>
            )}
            {(m.badges || []).length > 0 && (
              <span className="inline-flex gap-1 mr-1 align-middle" data-testid={`chat-badges-${m.username}`}>
                {m.badges.slice(0, 3).map(b => (
                  <span key={b.key} className="text-[8px] sc-mono px-1 py-0.5 rounded-sm border align-middle" style={{ borderColor: `${b.color}80`, color: b.color, lineHeight: 1 }}>{b.label}</span>
                ))}
              </span>
            )}
            <ChatMessageText text={m.text} />
          </div>
          )
        ))}
      </div>
      <div className="p-2 border-t border-white/10 flex gap-2 items-end relative">
        <input
          ref={inputRef}
          data-testid={TIDS.chatInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
          placeholder={user ? "Send a message" : "Log in to chat"}
          disabled={!user}
          className="sc-input flex-1 min-w-0"
          maxLength={500}
        />
        {user && (
          <EmoteButton onClick={() => setPickerOpen(v => !v)} active={pickerOpen} />
        )}
        <button data-testid={TIDS.chatSend} onClick={send} disabled={!user} className="sc-btn px-4">Chat</button>
        {pickerOpen && (
          <EmotePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onInsert={insertAtCaret} />
        )}
      </div>

      {/* Context menu */}
      {menu && (
        <div
          className="fixed z-50 glass border border-white/10 rounded-md p-1 min-w-[180px] shadow-2xl"
          style={{ left: menu.x, top: menu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between">
            <div className="sc-label truncate max-w-[130px]">{menu.username}</div>
            <button onClick={() => setMenu(null)}><X className="w-3 h-3 text-zinc-500" /></button>
          </div>
          <ChatMenuItem icon={UserPlus} label="Add friend" onClick={async () => { try { await social.addFriend(menu.username); } catch { /* */ } setMenu(null); }} testId={`chat-menu-friend-${menu.username}`} />
          <Link to={`/messages?to=${menu.username}`} onClick={() => setMenu(null)} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/5 rounded-sm" data-testid={`chat-menu-dm-${menu.username}`}>
            <MessageSquare className="w-3.5 h-3.5" /> Send message
          </Link>
          {social.isMuted(menu.username) ? (
            <ChatMenuItem icon={Volume2} label="Unmute" onClick={async () => { await social.unmuteUser(menu.username); setMenu(null); }} testId={`chat-menu-unmute-${menu.username}`} />
          ) : (
            <ChatMenuItem icon={VolumeX} label="Mute in chat" onClick={async () => { await social.muteUser(menu.username); setMenu(null); }} testId={`chat-menu-mute-${menu.username}`} />
          )}
          {social.isBlocked(menu.username) ? (
            <ChatMenuItem icon={Ban} label="Unblock" color="var(--sc-pink)" onClick={async () => { await social.unblockUser(menu.username); setMenu(null); }} testId={`chat-menu-unblock-${menu.username}`} />
          ) : (
            <ChatMenuItem icon={Ban} label="Block user" color="var(--sc-pink)" onClick={async () => { await social.blockUser(menu.username); setMenu(null); }} testId={`chat-menu-block-${menu.username}`} />
          )}
          {isStreamer && channel && (
            <>
              <div className="border-t border-white/10 my-1" />
              <div className="px-3 py-1 text-[9px] tracking-[0.2em] uppercase text-zinc-500">Moderation</div>
              <ChatMenuItem
                icon={LogOut}
                label="Kick from channel"
                color="var(--sc-orange)"
                onClick={async () => {
                  try { await api.post(`/channels/${channel.username}/kick`, { username: menu.username }); } catch { /* */ }
                  setMenu(null);
                }}
                testId={`chat-menu-kick-${menu.username}`}
              />
              <ChatMenuItem
                icon={ShieldOff}
                label="Ban from channel"
                color="var(--sc-pink)"
                onClick={async () => {
                  try { await api.post(`/channels/${channel.username}/ban`, { username: menu.username, reason: "" }); } catch { /* */ }
                  setMenu(null);
                }}
                testId={`chat-menu-chanban-${menu.username}`}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ChatMenuItem({ icon: Icon, label, onClick, color, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/5 rounded-sm text-left"
      style={{ color: color || undefined }}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
